// src/pages/affiliate/AffiliateAdminPage.tsx
// Admin area for the affiliate program — partners, offers, tiers,
// conversions and payouts. Gated by AdminRoute in App.tsx. Tab is
// URL-driven (?tab=…) like the calendar settings page.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { PartnersTab } from '@/features/affiliate/admin/PartnersTab';
import { OffersTab } from '@/features/affiliate/admin/OffersTab';
import { TiersTab } from '@/features/affiliate/admin/TiersTab';
import { ConversionsTab } from '@/features/affiliate/admin/ConversionsTab';
import { PayoutsTab } from '@/features/affiliate/admin/PayoutsTab';

type Tab = 'partners' | 'offers' | 'tiers' | 'conversions' | 'payouts';
const TABS: Tab[] = ['partners', 'offers', 'tiers', 'conversions', 'payouts'];

export const AffiliateAdminPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'partners';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('affiliate.admin.title')}</h1>
        <Link to="/affiliate" className="ml-auto text-sm text-primary hover:underline">
          {t('affiliate.backToDashboard')}
        </Link>
      </div>

      <div role="tablist" aria-label={t('affiliate.admin.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`affiliate.admin.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'partners' && <PartnersTab />}
      {tab === 'offers' && <OffersTab />}
      {tab === 'tiers' && <TiersTab />}
      {tab === 'conversions' && <ConversionsTab />}
      {tab === 'payouts' && <PayoutsTab />}
    </div>
  );
});
